import * as React from "react";
import classnames from "classnames";

import Eye from "@/components/SVGR/Eye";
import styles from "./styles.module.scss";

type NewInputType = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "value"
> & {
  onChange: (str: string) => void;
  value?: string | number;
};

export interface InputProps extends NewInputType {
  /**
   * if `true`, warning only cause to change border color
   * @default false
   */
  isSilentWarning?: boolean;
  warning?: React.ReactNode;
  chevronOrientation?: "right" | "bottom";
  onChange: (str: string) => void;
  icon?: React.ReactNode;
  ["data-testid"]?: string;
  showPassword?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      value,
      warning,
      isSilentWarning = false,
      "data-testid": dataTestId,
      chevronOrientation,
      disabled,
      maxLength,
      onChange,
      enterKeyHint,
      className,
      autoComplete,
      showPassword,
      icon,
      type,
      tabIndex = 0,
      ...otherProps
    },
    ref
  ) => {
    const [show, setShow] = React.useState<boolean>(false);
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    };

    return (
      <div>
        <div className="relative">
          {icon && icon}
          <input
            {...otherProps}
            ref={ref}
            tabIndex={tabIndex}
            enterKeyHint={enterKeyHint}
            disabled={disabled}
            autoComplete={autoComplete}
            maxLength={maxLength}
            className={classnames(className, styles.input, {
              [styles.haveEye]: showPassword,
              [styles.warning]: !disabled && warning,
              [styles.right]: chevronOrientation === "right",
              [styles.bottom]: chevronOrientation === "bottom",
              [styles.haveIcon]: icon,
            })}
            type={show ? "text" : type}
            data-testid={dataTestId}
            value={value || ""}
            onChange={handleChange}
          />
          {showPassword && (
            <button
              tabIndex={tabIndex}
              className={styles.eye}
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                setShow((prevState) => !prevState);
              }}
            >
              <Eye status={show} />
            </button>
          )}
        </div>
        {!disabled && warning && (
          <p
            className={classnames(
              "mt-1",
              styles.warningSpace,
              styles.warningMessage
            )}
          >
            {warning}
          </p>
        )}
      </div>
    );
  }
);
Input.displayName = "Input";

export default Input;
